import type { Editor } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import React, { useContext, useEffect } from 'react';

import type {
  AddLinkData,
  AvailableExperiments,
  EditorPlugin,
  Helpers,
  IRicosToolbars,
  LinkPanelSettings,
  LinkSettings,
  PublicCommands,
  RicosPortal,
  RicosTheme,
  TFunction,
  ToolbarSettings,
} from './../ricos-types';

import { EditorContext } from './EditorContext';
import { RicosContext } from './RicosContext';

export type LinkPanelData = {
  linkTypes: { anchor: boolean };
  onLinkAdd: (customLinkData: any, saveData: (customLinkData: any) => void) => void;
  uiSettings: { linkPanel: LinkPanelSettings };
  linkSettings: LinkSettings | undefined;
  isMobile: boolean;
  onAddPluginLink: (data: AddLinkData) => void;
};

type HeadingsData = {
  customHeadings: string[];
  allowHeadingCustomization: boolean;
};

export type ToolbarContextType = {
  contentId: string;
  isMobile: boolean;
  theme: RicosTheme;
  locale: string;
  helpers: Helpers;
  t: TFunction;
  plugins: EditorPlugin[];
  linkPanelData: LinkPanelData | undefined;
  headingsData: HeadingsData | undefined;
  linkSettings: LinkSettings;
  experiments: AvailableExperiments;
  toolbarSettings: ToolbarSettings;
  getPublicCommands: () => PublicCommands;
  portal: RicosPortal | null;
  editorRef: HTMLElement | null;
  toolbars: IRicosToolbars;
  defaultLineSpacing: { 'line-height'?: string; 'padding-top'?: string; 'padding-bottom'?: string };
};

export const ToolbarContext = React.createContext<ToolbarContextType>({
  contentId: '',
  isMobile: false,
  theme: {},
  locale: 'en',
  helpers: {},
  plugins: [],
  t: ((text: string) => text) as TFunction,
  linkPanelData: undefined,
  headingsData: undefined,
  linkSettings: {},
  experiments: {},
  toolbarSettings: {},
  getPublicCommands: () => ({} as PublicCommands),
  portal: null,
  editorRef: null,
  toolbars: null as unknown as IRicosToolbars,
  defaultLineSpacing: {},
});

const getSelectedNodes = (editor: Editor) => {
  const selection = editor.state.selection;
  const nodes: Node[] = [];
  editor.state.doc.nodesBetween(selection.from, selection.to, node => {
    nodes.push(node);
  });

  return nodes;
};

export const ToolbarContextProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ToolbarContextType;
}) => {
  const {
    adapter: { tiptapEditor },
  } = useContext(EditorContext);
  const { content } = useContext(RicosContext);
  const [_, setSelectedNodes] = React.useState<Node[]>([]);

  useEffect(() => {
    const updateContent = () => {
      const newSelectedNodes = getSelectedNodes(tiptapEditor);
      setSelectedNodes(newSelectedNodes);
      content.update(newSelectedNodes, tiptapEditor);
    };

    const onTransaction = ({ transaction }: { transaction: Transaction }) => {
      if (transaction.storedMarksSet) {
        updateContent();
      }
    };

    tiptapEditor.on('transaction', onTransaction);
    tiptapEditor.on('update', updateContent);
    tiptapEditor.on('focus', updateContent);
    tiptapEditor.on('selectionUpdate', updateContent);

    return () => {
      tiptapEditor.off('transaction', onTransaction);
      tiptapEditor.off('update', updateContent);
      tiptapEditor.off('focus', updateContent);
      tiptapEditor.off('selectionUpdate', updateContent);
    };
  }, []);

  return <ToolbarContext.Provider value={{ ...value }}>{children}</ToolbarContext.Provider>;
};

export const withToolbarContext = (WrappedComponent: $TSFixMe) => {
  return (props: $TSFixMe) => {
    const { t } = useContext(RicosContext);
    return (
      <ToolbarContext.Consumer>
        {contexts => <WrappedComponent {...props} context={{ ...contexts, t }} />}
      </ToolbarContext.Consumer>
    );
  };
};
